import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { heroMosaic, galleryImages } from '../mock';
import { useEmpezar } from './EmpezarModal';
import { getLangFromPath, localizePath } from '../i18n';

export default function HeroSection() {
  const { open: openEmpezar } = useEmpezar();
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);

  const heroPhotos = [
    ...heroMosaic.left,
    ...heroMosaic.right,
    ...galleryImages,
  ];
  const marqueeItems = [...heroPhotos, ...heroPhotos];

  return (
    <section className="relative overflow-hidden pt-10 lg:pt-14 pb-10 lg:pb-14">
      <div className="hero-glow" />
      <div className="absolute top-20 -left-32 w-[500px] h-[500px] bg-violet-600/15 rounded-full blur-[120px]" />
      <div className="absolute -bottom-20 -right-40 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[140px]" />

      <div className="container-mi relative z-10">
        <div className="max-w-3xl fade-in-up" data-testid="hero-content">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            {t('home.hero.title')}
            <span className="block mt-2 bg-gradient-to-r from-violet-300 via-violet-400 to-fuchsia-300 bg-clip-text text-transparent">
              {t('home.hero.titleHighlight')}
            </span>
          </h1>

          <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
            {t('home.hero.subtitle')}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-start gap-3">
            <Button
              onClick={openEmpezar}
              data-testid="hero-cta-start"
              className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold text-base group"
            >
              {t('home.hero.ctaStart')}
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Link to={localizePath('/request-demo', lang)}>
              <Button data-testid="hero-cta-demo" variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white text-base">
                {t('home.hero.ctaDemo')}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-12 lg:mt-16 relative overflow-hidden" data-testid="hero-marquee">
        <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-[#0a0a0f] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-[#0a0a0f] to-transparent z-10 pointer-events-none" />

        <div className="marquee-track flex gap-4 whitespace-nowrap">
          {marqueeItems.map((img, i) => (
            <div
              key={i}
              className="w-[240px] h-[300px] md:w-[280px] md:h-[350px] rounded-2xl overflow-hidden shrink-0 border border-white/10 shadow-2xl shadow-black/40"
            >
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
